/**
 * Taxonomy API Service (S1.1)
 *
 * Service layer for the 5-capable product-category tree + renamable level
 * config (S0.4 schema). Endpoints: /product-categories, /category-levels
 * (see api-spec.yaml — added in S0.4).
 *
 * NOTE: route strings are local until `api.constants.ts` (not owned by this
 * slice) gains PRODUCT_CATEGORIES / CATEGORY_LEVELS entries.
 *
 * CU-1b (taxonomy half of the CU-1 flip): every method gains a live-module
 * leg (the `taxonomy` key, client.ts) executing the contract operations
 * against `/graphql` (taxonomy.operations.ts → taxonomy.gql-map.ts).
 * SIGNATURES ARE UNCHANGED — consumers cannot tell which leg served them,
 * which is the flip's whole claim and what the L2 fe-seam shadow leg
 * byte-diffs. Refusals are rebuilt into the mock's own axios-shaped
 * `{error, message}` + status (LegacyGuardError), so `extractGuardError`
 * in `src/hooks/useTaxonomy.ts` keeps working untouched.
 */

import apiClient from './axios.config';
import { isLiveModule } from './client';
import { gqlMutate, gqlRequest } from './graphql.client';
import {
  taxonomyOperations,
  type CategoryDeactivateResult,
  type CategoryDetailResult,
  type CategoryFlatListResult,
  type CategoryFlatListVariables,
  type CategoryWriteResult,
  type TaxonomyAuditResult,
  type TaxonomyLevelUpdateResult,
  type TaxonomyLevelsResult,
} from './taxonomy.operations';
import {
  flattenTaxonomyTree,
  mapAuditEntry,
  mapLevelConfig,
  mapTaxonomyNode,
  sortForRead,
  throwNodeNotFound,
  throwTaxonomyGuard,
  toExternalMappingInput,
} from './taxonomy.gql-map';
import { selectableCategories } from '@/utils/categoryUtils';
import type {
  CategoryAuditListResponse,
  CategoryLevelListResponse,
  CategoryLevelConfig,
  CreateProductCategoryRequest,
  DeactivateProductCategoryRequest,
  DeactivateProductCategoryResponse,
  ProductCategoryDetailResponse,
  ProductCategoryListParams,
  ProductCategoryListResponse,
  ReparentProductCategoryRequest,
  UpdateCategoryLevelRequest,
  UpdateProductCategoryRequest,
} from '@/types/api/productCategory';

const CATEGORIES_ROUTE = '/product-categories';
const LEVELS_ROUTE = '/category-levels';

/**
 * Only keys the caller actually sent reach the graph input. The mock's PATCH
 * semantics are `??` — absent means "leave alone" — and a mutation input that
 * spelled every key as `null` would clear fields the caller never touched.
 */
function definedOnly<T extends Record<string, unknown>>(input: T): T {
  return Object.fromEntries(Object.entries(input).filter(([, value]) => value !== undefined)) as T;
}

export const taxonomyApi = {
  /**
   * GET /api/product-categories
   * Flat node list. `forSelection: true` = cascader new-selection set
   * (active nodes at enabled levels only — hidden-level read rule).
   */
  listCategories: async (
    params?: ProductCategoryListParams
  ): Promise<ProductCategoryListResponse> => {
    if (isLiveModule('taxonomy')) {
      // `forSelection` narrows over the FULL corpus (it filters isActive
      // itself), so the tree read asks for everything in that mode.
      const result = await gqlRequest<CategoryFlatListResult, CategoryFlatListVariables>(
        taxonomyOperations.categoryFlatList,
        { includeInactive: params?.forSelection === true || params?.includeInactive === true }
      );
      const nodes = flattenTaxonomyTree(result.productTaxonomyTree);
      if (params?.forSelection === true) {
        const levels = result.productTaxonomyLevelConfigs.map(mapLevelConfig);
        // The ONE shared predicate (src/utils/categoryUtils) — the same
        // function the mock handler and the picker call. No second rule.
        return { data: sortForRead(selectableCategories(nodes, levels)) };
      }
      return { data: sortForRead(nodes) };
    }
    const response = await apiClient.get<ProductCategoryListResponse>(CATEGORIES_ROUTE, {
      params,
    });
    return response.data;
  },

  /** GET /api/product-categories/:id */
  getCategory: async (id: string): Promise<ProductCategoryDetailResponse> => {
    if (isLiveModule('taxonomy')) {
      const result = await gqlRequest<CategoryDetailResult, { id: string }>(
        taxonomyOperations.categoryDetail,
        { id }
      );
      if (!result.productTaxonomyNode) throwNodeNotFound();
      return { data: mapTaxonomyNode(result.productTaxonomyNode) };
    }
    const response = await apiClient.get<ProductCategoryDetailResponse>(
      `${CATEGORIES_ROUTE}/${id}`
    );
    return response.data;
  },

  /** POST /api/product-categories */
  createCategory: async (
    data: CreateProductCategoryRequest
  ): Promise<ProductCategoryDetailResponse> => {
    if (isLiveModule('taxonomy')) {
      const result = await gqlMutate<CategoryWriteResult, { input: Record<string, unknown> }>(
        taxonomyOperations.categoryCreate,
        {
          input: definedOnly({
            code: data.code,
            nameEn: data.nameEn,
            nameZh: data.nameZh,
            parentId: data.parentId,
            sortOrder: data.sortOrder,
            netsuiteSegmentValueId: data.netsuiteSegmentValueId,
            // The graph makes the audit comment mandatory; the legacy wire
            // makes it optional on CREATE (the mock stores '' when absent).
            comment: data.comment ?? '',
          }),
        }
      );
      const payload = result.productCategoryCreate;
      throwTaxonomyGuard(payload?.userErrors ?? []);
      if (!payload?.category) throwNodeNotFound();
      return { data: mapTaxonomyNode(payload.category) };
    }
    const response = await apiClient.post<ProductCategoryDetailResponse>(CATEGORIES_ROUTE, data);
    return response.data;
  },

  /**
   * PATCH /api/product-categories/:id
   * Rename / resort / NetSuite ref — `code` is immutable; mandatory change comment.
   * Also carries the Slice 5.1 enrich fields (`description`/`iconKey`/`synonyms`/
   * `externalMappings`) as REAL columns since B2.3' (the OQ-4 gate is closed —
   * C4.3's enrich UI unblocks at CU-1).
   */
  updateCategory: async (
    id: string,
    data: UpdateProductCategoryRequest
  ): Promise<ProductCategoryDetailResponse> => {
    if (isLiveModule('taxonomy')) {
      const result = await gqlMutate<CategoryWriteResult, { input: Record<string, unknown> }>(
        taxonomyOperations.categoryUpdate,
        {
          input: definedOnly({
            categoryId: id,
            comment: data.comment,
            nameEn: data.nameEn,
            nameZh: data.nameZh,
            sortOrder: data.sortOrder,
            netsuiteSegmentValueId: data.netsuiteSegmentValueId,
            description: data.description,
            iconKey: data.iconKey,
            synonyms: data.synonyms,
            externalMappings: toExternalMappingInput(data.externalMappings),
          }),
        }
      );
      const payload = result.productCategoryUpdate;
      throwTaxonomyGuard(payload?.userErrors ?? []);
      if (!payload?.category) throwNodeNotFound();
      return { data: mapTaxonomyNode(payload.category) };
    }
    const response = await apiClient.patch<ProductCategoryDetailResponse>(
      `${CATEGORIES_ROUTE}/${id}`,
      data
    );
    return response.data;
  },

  /** POST /api/product-categories/:id/reparent — depth/path recomputed server-side. */
  reparentCategory: async (
    id: string,
    data: ReparentProductCategoryRequest
  ): Promise<ProductCategoryDetailResponse> => {
    if (isLiveModule('taxonomy')) {
      const result = await gqlMutate<CategoryWriteResult, { input: Record<string, unknown> }>(
        taxonomyOperations.categoryReparent,
        {
          input: definedOnly({
            categoryId: id,
            newParentId: data.newParentId,
            comment: data.comment,
          }),
        }
      );
      const payload = result.productCategoryReparent;
      throwTaxonomyGuard(payload?.userErrors ?? []);
      if (!payload?.category) throwNodeNotFound();
      return { data: mapTaxonomyNode(payload.category) };
    }
    const response = await apiClient.post<ProductCategoryDetailResponse>(
      `${CATEGORIES_ROUTE}/${id}/reparent`,
      data
    );
    return response.data;
  },

  /**
   * POST /api/product-categories/:id/deactivate
   * No hard delete exists. Replacement required whenever products are
   * assigned to the node or its subtree (server answers REPLACEMENT_REQUIRED).
   */
  deactivateCategory: async (
    id: string,
    data: DeactivateProductCategoryRequest
  ): Promise<DeactivateProductCategoryResponse> => {
    if (isLiveModule('taxonomy')) {
      const result = await gqlMutate<CategoryDeactivateResult, { input: Record<string, unknown> }>(
        taxonomyOperations.categoryDeactivate,
        {
          input: definedOnly({
            categoryId: id,
            replacementId: data.replacementId,
            comment: data.comment,
          }),
        }
      );
      const payload = result.productCategoryDeactivate;
      throwTaxonomyGuard(payload.userErrors);
      if (!payload.category) throwNodeNotFound();
      return {
        data: mapTaxonomyNode(payload.category),
        reassignedProducts: payload.reassignedProducts,
      };
    }
    const response = await apiClient.post<DeactivateProductCategoryResponse>(
      `${CATEGORIES_ROUTE}/${id}/deactivate`,
      data
    );
    return response.data;
  },

  /** GET /api/category-levels — the 5 renamable bilingual level labels. */
  listLevels: async (): Promise<CategoryLevelListResponse> => {
    if (isLiveModule('taxonomy')) {
      const result = await gqlRequest<TaxonomyLevelsResult, Record<string, never>>(
        taxonomyOperations.levels,
        {}
      );
      return { data: result.productTaxonomyLevelConfigs.map(mapLevelConfig) };
    }
    const response = await apiClient.get<CategoryLevelListResponse>(LEVELS_ROUTE);
    return response.data;
  },

  /** PATCH /api/category-levels/:level — rename + L4/L5 enablement (L1..L3 locked on). */
  updateLevel: async (
    level: number,
    data: UpdateCategoryLevelRequest
  ): Promise<{ data: CategoryLevelConfig }> => {
    if (isLiveModule('taxonomy')) {
      const result = await gqlMutate<TaxonomyLevelUpdateResult, { input: Record<string, unknown> }>(
        taxonomyOperations.levelUpdate,
        {
          input: definedOnly({
            depth: level,
            comment: data.comment,
            labelEn: data.labelEn,
            labelZh: data.labelZh,
            enabled: data.enabled,
          }),
        }
      );
      const payload = result.productTaxonomyLevelUpdate;
      throwTaxonomyGuard(payload.userErrors);
      if (!payload.level) throwNodeNotFound();
      return { data: mapLevelConfig(payload.level) };
    }
    const response = await apiClient.patch<{ data: CategoryLevelConfig }>(
      `${LEVELS_ROUTE}/${level}`,
      data
    );
    return response.data;
  },

  /** GET /api/product-categories/audit — every taxonomy mutation writes a row. */
  listAudit: async (): Promise<CategoryAuditListResponse> => {
    if (isLiveModule('taxonomy')) {
      const result = await gqlRequest<TaxonomyAuditResult, { targetId?: string }>(
        taxonomyOperations.auditFeed,
        {}
      );
      return { data: result.productTaxonomyAudit.map(mapAuditEntry) };
    }
    const response = await apiClient.get<CategoryAuditListResponse>(`${CATEGORIES_ROUTE}/audit`);
    return response.data;
  },
};

export default taxonomyApi;
